import React, { ChangeEvent, KeyboardEvent, Component, RefObject, createRef } from 'react'
import hashUtil from '../utils/hash.util'
import InputIcon from './InputIcon'
import InputLabel from './InputLabel'

type InputType = 'text' | 'email' | 'password' | 'number'

export type InputTextRef = RefObject<HTMLInputElement>

export interface InputTextProps {
  name: string
  value: string
  label?: string
  type?: InputType
  placeholder?: string
  readonly?: boolean
  autofocus?: boolean
  autoFocusCallback? : () => void
  icon?: string
  customRef?: InputTextRef
  onChange?: (name: string, value: string) => void
  onFocus?: (name: string, value: string) => void
  onBlur?: (name: string) => void
  onScan?: (name: string, value: string) => void
  onIconClick?: (name: string) => void
  enforceLimits?: boolean
}

export default class InputText extends Component<InputTextProps> {
  inputRef: RefObject<HTMLInputElement>

  constructor (props: InputTextProps) {
    super(props)
    this.inputRef = this.props.customRef || createRef()
    this.handleChange = this.handleChange.bind(this)
    this.handleFocus = this.handleFocus.bind(this)
    this.handleBlur = this.handleBlur.bind(this)
    this.handleKeyUp = this.handleKeyUp.bind(this)
    this.handleIconClick = this.handleIconClick.bind(this)
  }

  get className () {
    const base = 'input-default'
    const readonly = this.props.readonly && 'bg-gray-50'
    return `${base} ${readonly}`
  }

  handleChange (event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target
    const { name, onChange } = this.props
    onChange && onChange(name, value)
  }

  handleFocus (event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.target
    const { name, onFocus } = this.props
    onFocus && onFocus(name, value)
  }

  handleBlur () {
    const { name, onBlur } = this.props
    onBlur && onBlur(name)
  }

  handleKeyUp (event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      const { value } = event.target as HTMLInputElement
      const { name, onScan } = this.props
      if (onScan) onScan(name, value)
    }
  }

  handleIconClick () {
    const { name, onIconClick } = this.props
    onIconClick && onIconClick(name)
  }

  get name () {
    return `${hashUtil.unique()}-${this.props.name}`
  }

  autofocus () {
    const { autofocus } = this.props
    if (!autofocus) return
    const inputElement = this.inputRef.current
    if (!inputElement) return
    inputElement.focus()

    if (this.props.autoFocusCallback){
      this.props.autoFocusCallback()
    }
  }

  componentDidMount () {
    this.autofocus()
  }

 inputProps = this.props.enforceLimits ? { min: 0, max: 1, step: 0.01 } : {};

  render () {
    const { label, icon, value, type, placeholder } = this.props
    const { readonly, onIconClick } = this.props
    return (
      <div className="block w-full">
        <InputLabel text={label} />
        <InputIcon
          name={icon}
          onClick={onIconClick ? this.handleIconClick : undefined}>
          <input
            className={this.className}
            name={this.name}
            value={value}
            type={type}
            placeholder={placeholder}
            readOnly={readonly}
            onChange={this.handleChange}
            onKeyUp={this.handleKeyUp}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            ref={this.inputRef}
            autoComplete={this.name}
            {...this.inputProps} />
        </InputIcon>
      </div>
    )
  }
}
